// CheckBalancePage.jsx
import { useState } from 'react';
import { Search, CreditCard, Calendar, DollarSign } from 'lucide-react';
import { fetchData } from 'helpers/giftCardAPI';
import { summaryApi } from 'common';
import { useTheme } from "../../components/Theme/ThemeProvider";

export function CheckBalancePage() {
  const [code, setCode] = useState('');
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { colors } = useTheme();

  const checkBalance = async () => {
    if (!code.trim()) {
      setError('Please enter a gift card code');
      return;
    }

    setLoading(true);
    setError('');
    setBalance(null);

    try {
      const url = summaryApi.checkGiftCardBalance.url.replace(':code', code);
      const data = await fetchData(url);

      if (data.success) {
        setBalance(data.data);
      } else {
        setError(data.message || 'Gift card not found');
      }
    } catch (err) {
      setError('Failed to check balance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return { bg: `${colors.success}20`, text: colors.success };
      case 'redeemed': return { bg: `${colors.textSecondary}20`, text: colors.textSecondary };
      case 'expired': return { bg: `${colors.error}20`, text: colors.error };
      case 'cancelled': return { bg: `${colors.warning}20`, text: colors.warning };
      default: return { bg: `${colors.textSecondary}20`, text: colors.textSecondary };
    }
  };

  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: colors.bgPrimary }}>
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: colors.text }}>
            Check Gift Card Balance
          </h1>
          <p style={{ color: colors.textSecondary }}>
            Enter your gift card code to view your balance
          </p>
        </div>

        <div className="rounded-lg shadow-lg p-8" style={{ backgroundColor: colors.bgSecondary }}>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              Gift Card Code
            </label>
            <div className="flex gap-3">
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && checkBalance()}
                placeholder="GC-XXXX-XXXX-XXXX"
                className="flex-1 px-4 py-3 rounded-lg font-mono focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: colors.bgPrimary,
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                  '--tw-ring-color': colors.primary
                }}
                maxLength={20}
              />
              <button
                onClick={checkBalance}
                disabled={loading}
                className="px-6 py-3 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                style={{ backgroundColor: colors.primary }}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    Check Balance
                  </>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="p-4 border rounded-lg mb-6" style={{ 
              backgroundColor: `${colors.error}10`,
              borderColor: colors.error
            }}>
              <p className="text-sm" style={{ color: colors.error }}>{error}</p>
            </div>
          )}

          {balance && (
            <div className="space-y-4">
              <div className="rounded-lg p-6 text-white" style={{
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
              }}>
                <div className="flex items-center justify-between mb-4">
                  <CreditCard className="w-8 h-8" />
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: getStatusColor(balance.status).bg,
                      color: getStatusColor(balance.status).text
                    }}
                  >
                    {balance.status.toUpperCase()}
                  </span>
                </div>
                <p className="text-sm opacity-90 mb-2">Current Balance</p>
                <p className="text-4xl font-bold mb-4">₦{balance.currentBalance.toLocaleString()}</p>
                <p className="text-sm opacity-75 font-mono">{balance.code}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg p-4" style={{ backgroundColor: colors.bgTertiary }}>
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4" style={{ color: colors.textSecondary }} />
                    <p className="text-sm" style={{ color: colors.textSecondary }}>Original Amount</p>
                  </div>
                  <p className="text-xl font-semibold" style={{ color: colors.text }}>
                    ₦{balance.originalAmount.toLocaleString()}
                  </p>
                </div>

                <div className="rounded-lg p-4" style={{ backgroundColor: colors.bgTertiary }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4" style={{ color: colors.textSecondary }} />
                    <p className="text-sm" style={{ color: colors.textSecondary }}>Expires On</p>
                  </div>
                  <p className="text-sm font-semibold" style={{ color: colors.text }}>
                    {new Date(balance.expiryDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>

              {balance.currentBalance > 0 && balance.status === 'active' && (
                <div className="border rounded-lg p-4" style={{
                  backgroundColor: `${colors.primary}10`,
                  borderColor: colors.primary
                }}>
                  <p className="text-sm" style={{ color: colors.text }}>
                    <strong>Ready to use!</strong> Use this code at checkout to apply your gift card balance.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
